import React, { useState } from "react";

import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  Paper,
  IconButton,
  Typography,
  Grid,
  Button,
  Select,
  MenuItem,
  Stack,
  Box,
  styled,
} from "@mui/material";
import dayjs from "dayjs";

import ConfirmButton from "../common/Button";

// Styled components
const DayButton = styled(Button)(({ theme, selected, isToday }) => ({
  minWidth: 40,
  width: 40,
  height: 40,
  padding: 0,
  color: isToday ? theme.palette.primary.main : "white",
  backgroundColor: selected ? "#323b43" : "transparent",
  "&:hover": {
    backgroundColor: selected ? "#323b43" : theme.palette.action.hover,
  },
  "& .MuiButton-label": {
    color: selected ? theme.palette.primary.contrastText : "inherit",
  },
}));

const TimeSelect = styled(Select)({
  "& .MuiSelect-select": {
    padding: "5px 24px 5px 12px",
  },
  minWidth: 10,
});

const menuProps = {
  PaperProps: {
    style: {
      maxHeight: 250, // Limit the height of the dropdown
    },
  },
};

const CustomDateTimePicker = ({ value, onChange, onAccept, minDate, isLoading }) => {
  const [currentDate, setCurrentDate] = useState(
    value ? dayjs(value).toDate() : new Date()
  );
  const [selectedDate, setSelectedDate] = useState(
    value ? dayjs(value).toDate() : null
  );
  const [selectedHour, setSelectedHour] = useState(
    value ? dayjs(value).format("hh") : "12"
  );
  const [selectedMinute, setSelectedMinute] = useState(
    value ? dayjs(value).format("mm") : "00"
  );
  const [selectedPeriod, setSelectedPeriod] = useState(
    value ? dayjs(value).format("A") : "PM"
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const generateTimeOptions = (start, end) => {
    const options = [];
    for (let i = start; i <= end; i++) {
      options.push(i.toString().padStart(2, "0"));
    }
    return options;
  };

  const hours = generateTimeOptions(1, 12);
  const minutes = generateTimeOptions(0, 59);

  const previousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  };

  const handleDateTimeChange = (date, hour, minute, period) => {
    if (!date) return;

    const timeString = `${hour}:${minute} ${period}`;
    const dateTimeString = `${date.getMonth() + 1
      }/${date.getDate()}/${date.getFullYear()} ${timeString}`;
    const newDateTime = dayjs(dateTimeString, "M/D/YYYY hh:mm A");

    if (onChange) {
      onChange(newDateTime);
    }
  };

  const handleAccept = () => {
    if (!selectedDate) return;

    const timeString = `${selectedHour}:${selectedMinute} ${selectedPeriod}`;
    const dateTimeString = `${selectedDate.getMonth() + 1
      }/${selectedDate.getDate()}/${selectedDate.getFullYear()} ${timeString}`;
    const newDateTime = dayjs(dateTimeString, "M/D/YYYY hh:mm A");

    if (onAccept) {
      onAccept(newDateTime);
    }
  };

  const renderCalendarDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);
    const today = new Date();
    const minDateTime = minDate ? dayjs(minDate).startOf("day") : null;

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="text.disabled">
            {new Date(
              currentDate.getFullYear(),
              currentDate.getMonth() - 1,
              daysInMonth(
                new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
              ) -
              firstDay +
              i +
              1
            ).getDate()}
          </Typography>
        </Box>
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= totalDays; day++) {
      const currentDayDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      const isDisabled =
        minDateTime && dayjs(currentDayDate).isBefore(minDateTime);

      const isSelected =
        selectedDate?.getDate() === day &&
        selectedDate?.getMonth() === currentDate.getMonth() &&
        selectedDate?.getFullYear() === currentDate.getFullYear();

      const isToday =
        today.getDate() === day &&
        today.getMonth() === currentDate.getMonth() &&
        today.getFullYear() === currentDate.getFullYear();

      days.push(
        <DayButton
          key={day}
          onClick={() => {
            if (!isDisabled) {
              const newDate = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                day
              );
              setSelectedDate(newDate);
              handleDateTimeChange(
                newDate,
                selectedHour,
                selectedMinute,
                selectedPeriod
              );
            }
          }}
          selected={isSelected}
          isToday={isToday}
          disabled={isDisabled}
          variant={isSelected ? "contained" : "text"}
          sx={{
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          <Typography
            variant="body2"
            color={isSelected ? "inherit" : "textPrimary"}
          >
            {day}
          </Typography>
        </DayButton>
      );
    }

    return days;
  };

  return (
    <Paper elevation={3} sx={{ width: 320, height: '25rem', p: 2, bgcolor: "#FBFBFB" }}>
      <Stack spacing={2}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </Typography>
          <Box>
            <IconButton size="small" onClick={previousMonth}>
              <ChevronLeft />
            </IconButton>
            <IconButton size="small" onClick={nextMonth}>
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>

        {/* Weekday headers */}
        <Grid container spacing={0} justifyContent="center">
          {["S", "M", "T", "W", "T", "F", "S"].map((day) => (
            <Grid item key={day} sx={{ width: 40 }}>
              <Typography
                variant="caption"
                align="center"
                sx={{ display: "block" }}
                color="text.secondary"
              >
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Calendar grid */}
        <Grid container spacing={0} justifyContent="center">
          {renderCalendarDays()}
        </Grid>

        {/* Time selection */}
        <Stack direction="row" spacing={1} justifyContent="center">
          <TimeSelect
            value={selectedHour}
            onChange={(e) => {
              setSelectedHour(e.target.value);
              handleDateTimeChange(
                selectedDate,
                e.target.value,
                selectedMinute,
                selectedPeriod
              );
            }}
            size="small"
            MenuProps={menuProps} // Pass the custom menu props
          >
            {hours.map((hour) => (
              <MenuItem key={hour} value={hour}>
                {hour}
              </MenuItem>
            ))}
          </TimeSelect>
          <TimeSelect
            value={selectedMinute}
            onChange={(e) => {
              setSelectedMinute(e.target.value);
              handleDateTimeChange(
                selectedDate,
                selectedHour,
                e.target.value,
                selectedPeriod
              );
            }}
            size="small"
            MenuProps={menuProps} // Pass the custom menu props
          >
            {minutes.map((minute) => (
              <MenuItem key={minute} value={minute}>
                {minute}
              </MenuItem>
            ))}
          </TimeSelect>
          <TimeSelect
            value={selectedPeriod}
            onChange={(e) => {
              setSelectedPeriod(e.target.value);
              handleDateTimeChange(
                selectedDate,
                selectedHour,
                selectedMinute,
                e.target.value
              );
            }}
            size="small"
            MenuProps={menuProps} // Pass the custom menu props
          >
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </TimeSelect>
        </Stack>

        {/* Confirm button */}

        <Box display="flex" justifyContent="center">
          <ConfirmButton
            bgcolor="#4BC7E1"
            onClick={handleAccept}
            textStyles={{
              color: "white",
            }}
            size="small"
            isLoading={isLoading}
          >
            Confirm
          </ConfirmButton>
        </Box>
      </Stack>
    </Paper>
  );
};

export default CustomDateTimePicker;
